/**
 * This week's view on one unit: its counts, its cadence lines, its subtree asks, read-only members.
 * The page's heading and its two roster panels are this screen's; everything under them is the
 * shared panel set, so a unit and a contract sit in the same column at the same padding.
 *
 * Counts, never ranks. No customer send. Edit membership on Structure.
 * docs/2026-09-20-program-structure.md
 */
import type { UnitView } from "@trellis/core";
import { plural } from "@trellis/core";
import { CountStrip, ProgramScreen, type AssignProps, type CountCell } from "./program.js";

export function UnitWeekScreen({ view, onGo, canAdmin = false, assign }: { view: UnitView; onGo: (path: string) => void; canAdmin?: boolean; assign?: AssignProps | undefined }) {
  // Tolerated as absent: an API that has not been redeployed yet still serves a view
  // without them, and a screen that throws on a missing optional is worse than one
  // that shows a little less.
  const path = view.path ?? [];
  const children = view.children ?? [];
  const unitPath = (id: string) => `/app/programs/${encodeURIComponent(view.programId)}/units/${encodeURIComponent(id)}`;
  const kind = view.unit.kind === "team" ? "Team" : "Workstream";
  const line = view.line;
  const parent = path[path.length - 1];
  const lead = view.roles.find((r) => r.roleId === "delivery-lead")?.holder;
  const tech = view.roles.find((r) => r.roleId === "technical-lead")?.holder;
  const w = view.window;
  const counts: CountCell[] = [
    { n: String(view.openCount), t: "open", what: "Confirmed work on this unit and anything under it, not closed." },
    { n: String(w.closed), t: `closed in ${w.days} days`, what: `${w.closedOnDate} of them closed on the date first accepted.` },
    { n: String(view.overdueCount), t: "late", what: "Open work past its date.", watch: view.overdueCount > 0 },
    { n: String(view.members.length), t: "people", what: "Everybody sitting on this unit or anything under it. A person on two teams is counted once." },
  ];

  const head = (
    <div class="contract-head">
      <p class="note dim">
        <button type="button" class="link" onClick={() => onGo(`/app/programs/${encodeURIComponent(view.programId)}`)}>{view.programName}</button>
        {" › "}
        {path.map((p) => (
          <span key={p.id}>
            <button type="button" class="link" onClick={() => onGo(unitPath(p.id))}>{p.name}</button>
            {" › "}
          </span>
        ))}
        {kind}
      </p>
      <div class="contract-title">
        <h1 class="h-lg">{view.unit.name}</h1>
        <p class="note dim code">{line ? [`CLIN ${line.key}`, line.name].filter(Boolean).join(" · ") : "On no contract line"}</p>
      </div>
      <p class="lede">
        {[
          `${kind}${parent ? ` under ${parent.name}` : ""}.`,
          lead ? `${lead.name} is delivery lead${tech ? `; ${tech.name} is technical lead` : ""}.` : "Nobody holds delivery lead.",
          `Everything below is scoped to this unit and the ${plural(children.length, "team")} under it; the contract's own events, linked systems and model gaps are on its page.`,
        ].join(" ")}
      </p>
      <p class="note dim">{view.ruleVersion} · {view.sources.join(" · ")}</p>
    </div>
  );

  const intro = (
    <div class="grid2">
      <section class="panel">
        <div class="panel-head"><span class="label">On this unit</span><span class="grow" /><span class="note dim">{plural(view.members.length, "person", "people")}</span></div>
        {view.members.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">Nobody sits here yet.</span></div>}
        {view.members.map((m) => (
          <div class="line" key={m.upn}><span class="dot navy" /><span class="body">{m.name}</span></div>
        ))}
        <div class="panel-foot">
          {canAdmin && <><button class="link" onClick={() => onGo(`/app/programs/${encodeURIComponent(view.programId)}/structure`)}>Edit on Structure</button>{" · "}</>}
          Counted across this unit and anything under it.
        </div>
      </section>

      <section class="panel">
        <div class="panel-head"><span class="label">Teams under it</span><span class="grow" /><span class="note dim">{plural(children.length, "team")}</span></div>
        {children.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">None. This is the bottom of this branch.</span></div>}
        {children.map((c) => (
          <button type="button" class="line unit-pick" key={c.id} onClick={() => onGo(unitPath(c.id))}>
            <span class="dot navy" />
            <span class="body">{c.name}<span class="s">{plural(c.people, "person", "people")}</span></span>
          </button>
        ))}
      </section>
    </div>
  );

  return (
    <ProgramScreen
      view={view}
      chrome="panels"
      head={<>{head}<CountStrip cells={counts} /></>}
      intro={intro}
      {...(assign ? { assign } : {})}
    />
  );
}
